import {
  keepPreviousData,
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ROUTES } from "@via/utils/constants";
import { createMongoFilter } from "@via/utils/helpers";
import {
  type ErrorNotification,
  type Pagination,
} from "@via/utils/interfaces.tsx";
import {
  CreateVmetaProjectRequestBody,
  GetProjectResponseBodyItem,
  UpdateVmetaProjectRequestBody,
  VmetaProjectResponseBody,
  type CreateProjectRequestBody,
  type ListProjectsResponseBodyItem,
  type ProjectRunResponse,
  type ShareProjectRequestBody,
  type ShareProjectResponseBody,
  type VmetaProjectsResponseBody,
  type VmetaCanvasHasFileCollectionRequestBody,
  type VmetaCanvasHasFileCollectionResponseBody,
} from "common";
import { useNavigate } from "react-router-dom";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";

export type CheckVmetaCanvasWritePermissionResponseBody = {
  status: string;
  data: {
    writeAllowed: boolean;
    inUseDatasets: { id: string; name: string; fileIds: string[] }[];
  };
};

// Fetch project list
export const fetchProjectList = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: Array<{ id: string; value: unknown }>
): Promise<Pagination<ListProjectsResponseBodyItem>> => {
  try {
    const baseUrl = "/api/project/v1";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    if (sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }

    if (filter && filter.length > 0) {
      filter.map(f => {
        queryParams.append(f.id, f.value as string);
      });
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Fetch a single project -- side effect in our fetch from https://stackoverflow.com/questions/76961108/react-query-onsuccess-onerror-onsettled-are-deprecated-what-should-i-use-ins
export const fetchProject = async (
  id: string
): Promise<GetProjectResponseBodyItem> => {
  try {
    const res = await axios.get(`/api/project/v1/${id}`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Fetch project run list
export const fetchProjectRunList = async (
  id: string,
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  tagIds: string[] = []
): Promise<Pagination<ProjectRunResponse>> => {
  try {
    const baseUrl = `/api/project/v1/${id}/runs`;
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    if (sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }
    tagIds.forEach(tagId => queryParams.append("tagIds", tagId));

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Fetch project list for runs
export const fetchProjectListForRuns = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: Array<{ id: string; value: unknown }>
): Promise<Pagination<ListProjectsResponseBodyItem>> => {
  try {
    const baseUrl = "/api/project/v1/for-runs";
    const queryParams = new URLSearchParams({
      take: `${take}`,
      skip: `${skip}`,
    });

    if (sort.length > 0) {
      queryParams.append("sort", sort[0].id);
      queryParams.append("order", sort[0].desc ? "desc" : "asc");
    }

    if (filter && filter.length > 0) {
      filter.map(f => {
        queryParams.append(f.id, f.value as string);
      });
    }

    const url = `${baseUrl}?${queryParams.toString()}`;
    const res = await axios.get(url);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

// Hook for project list
export const useProjectList = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true
): UseQueryResult<Pagination<ListProjectsResponseBodyItem>, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<Pagination<ListProjectsResponseBodyItem>, Error>({
    queryKey: ["/api/project/v1", take, skip, sort, filter],
    queryFn: async () => await fetchProjectList(take, skip, sort, filter),
    retry: false,
    enabled,
  });
};

// Hook for a single project
export const useProject = (
  id: string
): UseQueryResult<GetProjectResponseBodyItem, ErrorNotification> => {
  return useQuery<GetProjectResponseBodyItem, ErrorNotification>({
    queryKey: ["/api/project/v1", id],
    queryFn: async () => await fetchProject(id),
    retry: false,
  });
};

// Hook for project run list
export const useProjectRunList = (
  id: string,
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  tagIds: string[] = [],
  enabled: boolean = true
): UseQueryResult<Pagination<ProjectRunResponse>, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<Pagination<ProjectRunResponse>, Error>({
    queryKey: [
      "runList",
      `/api/project/v1/${id}/runs`,
      take,
      skip,
      sort,
      tagIds,
    ],
    queryFn: async () =>
      await fetchProjectRunList(id, take, skip, sort, tagIds),
    retry: true,
    retryOnMount: true,
    enabled,
  });
};

// Hook for project list for runs
export const useProjectListForRuns = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = []
): UseQueryResult<Pagination<ListProjectsResponseBodyItem>, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<Pagination<ListProjectsResponseBodyItem>, Error>({
    queryKey: ["/api/project/v1/for-runs", take, skip, sort, filter],
    queryFn: async () =>
      await fetchProjectListForRuns(take, skip, sort, filter),
    retry: false,
  });
};

// Hook for project run list
export const useRunList = (
  id: string,
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = []
): UseQueryResult<Pagination<ProjectRunResponse>, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<Pagination<ProjectRunResponse>, Error>({
    queryKey: [`/api/project/v1/${id}/runs`, take, skip, sort],
    queryFn: async () => await fetchProjectRunList(id, take, skip, sort),
    retry: false,
  });
};

export const useCreateProject = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async (payload: CreateProjectRequestBody) => {
      const res = await axios.post("/api/project/v1", payload);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "project-created",
        message: "Project Created",
        details: "Project has been created",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch all requests starts with "/api/project/v1"
      queryClient.invalidateQueries({
        predicate: query =>
          query.queryKey.some(
            key => typeof key === "string" && key.startsWith("/api/project/v1")
          ),
      });
    },
    onError: error => {
      setNotification({
        id: "project-creation-failed",
        message: "Project Creation Failed",
        details: "Project could not be created",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateProject = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: Record<string, string>;
    }) => {
      const res = await axios.patch(`/api/project/v1/${id}`, data);
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "project-updated",
        message: "Project Successfully Updated",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({ queryKey: ["/api/project/v1"] });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "project-update-failed",
        message: "Project Update Failed",
        details: error?.response?.data?.message,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useDeleteProject = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (id: string) => {
      return await axios.delete(`/api/project/v1/${id}`);
    },
    onSuccess: (): void => {
      setNotification({
        id: "project-deleted",
        message: "Project Deleted",
        details: "Project has been deleted",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project list
      queryClient.invalidateQueries({ queryKey: ["/api/project/v1"] });
      navigate(ROUTES.PROJECTS);
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "project-deletion-failed",
        message: "Project Delete Failed",
        details: error?.response?.data?.message,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useAddPipelineToProject = (): any => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation({
    mutationFn: async ({
      projectId,
      pipelineId,
    }: {
      projectId: string | undefined;
      pipelineId: string;
    }) => {
      const res = await axios.get(
        `/api/project/v1/${projectId}/pipeline/${pipelineId}`
      );
      return res.data;
    },
    onSuccess: (): void => {
      setNotification({
        id: "pipeline-added",
        message: "Pipeline Added",
        details: "Pipeline has been added to the project",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the pipeline list
      queryClient.invalidateQueries({
        queryKey: ["pipelineList"],
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message;
      setNotification({
        id: "pipeline-add-failed",
        message: "Pipeline could not be added",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchVmetaProjects = async (
  take: number,
  skip: number,
  sort: Array<{ id: string; desc: boolean }>,
  filter?: Array<{ id: string; value: unknown }>,
  sampleTrackerViewType?: SampleTrackerViewType,
  mongoUserId?: string
): Promise<VmetaProjectsResponseBody> => {
  try {
    const baseUrl = "/api/v1/vmeta/canvas/search";
    const payload: any = {
      take,
      skip,
    };
    if (sort.length > 0) {
      payload.sort = sort[0].id;
      payload.order = sort[0].desc ? "desc" : "asc";
    }

    const mongoFilter: Record<string, any> = {
      ...((filter && filter.length > 0
        ? createMongoFilter(filter, {
            name: "regex",
            _id: "eq",
            "owner._id": "eq",
          })
        : {}) || {}),
    };

    if (sampleTrackerViewType && mongoUserId) {
      switch (sampleTrackerViewType) {
        case SampleTrackerViewType.My:
          mongoFilter["owner._id"] = { eq: mongoUserId };
          break;
        case SampleTrackerViewType.Shared:
          mongoFilter["owner._id"] = { ne: mongoUserId };
          mongoFilter["perms.read.user"] = { nin: "everyone" };
          break;
        case SampleTrackerViewType.Public:
          mongoFilter["perms.read.user"] = { in: "everyone" };
          break;
      }
    }

    if (mongoFilter && Object.keys(mongoFilter).length > 0) {
      payload.filter = mongoFilter;
    }

    const res = await axios.post(baseUrl, payload);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useVmetaProjects = (
  page: number = 0,
  pageSize: number = 10,
  sort: Array<{ id: string; desc: boolean }> = [],
  filter: Array<{ id: string; value: unknown }> = [],
  enabled: boolean = true,
  sampleTrackerViewType?: SampleTrackerViewType,
  mongoUserId?: string
): UseQueryResult<VmetaProjectsResponseBody, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<VmetaProjectsResponseBody, Error>({
    queryKey: [
      "/api/v1/vmeta/canvas/search",
      take,
      skip,
      sort,
      filter,
      sampleTrackerViewType,
      mongoUserId,
    ],
    queryFn: async () =>
      await fetchVmetaProjects(
        take,
        skip,
        sort,
        filter,
        sampleTrackerViewType,
        mongoUserId
      ),
    retry: false,
    enabled,
  });
};

export const fetchVmetaCanvasesHasFileCollection = async (
  canvasIds: string[]
): Promise<VmetaCanvasHasFileCollectionResponseBody> => {
  const baseUrl = "/api/v1/vmeta/canvas/search/hasFileCollection";
  const payload: VmetaCanvasHasFileCollectionRequestBody = {
    canvasIds,
  };
  const res = await axios.post(baseUrl, payload);
  return res.data;
};

export const useVmetaCanvasesHasFileCollection = (
  canvasIds: string[],
  enabled: boolean = true
): UseQueryResult<VmetaCanvasHasFileCollectionResponseBody, Error> => {
  return useQuery<VmetaCanvasHasFileCollectionResponseBody, Error>({
    queryKey: ["/api/v1/vmeta/canvas/search/hasFileCollection", canvasIds],
    queryFn: async () => await fetchVmetaCanvasesHasFileCollection(canvasIds),
    retry: false,
    enabled: enabled && Array.isArray(canvasIds) && canvasIds.length > 0,
    // The canvas id list is part of the key, so every change starts a fresh
    // cache entry whose data is undefined until it answers. Callers read that
    // gap as "no canvas is allowed" and drop their whole scope, so hold the
    // previous answer until the new one lands.
    placeholderData: keepPreviousData,
  });
};

export const useCreateVmetadataTracker = (): UseMutationResult<
  VmetaProjectResponseBody,
  ErrorNotification,
  CreateVmetaProjectRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    VmetaProjectResponseBody,
    ErrorNotification,
    CreateVmetaProjectRequestBody
  >({
    mutationFn: async (
      payload: CreateVmetaProjectRequestBody
    ): Promise<VmetaProjectResponseBody> => {
      const res = await axios.post("/api/v1/vmeta/canvas/create", payload);
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "create-vmetada-tracker-successfully",
        message: "Sample Tracker Created",
        details: "Sample Tracker has been created",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the pipeline list
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/canvas/search"],
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An error occurred while creating the Sample Tracker";
      setNotification({
        id: "create-vmetadata-tracker-failed",
        message: "Sample Tracker Creation Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useShareProject = ({
  projectId,
}: {
  projectId: string;
}): UseMutationResult<
  ShareProjectResponseBody,
  ErrorNotification,
  ShareProjectRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    ShareProjectResponseBody,
    ErrorNotification,
    ShareProjectRequestBody
  >({
    mutationFn: async (
      payload: ShareProjectRequestBody
    ): Promise<ShareProjectResponseBody> => {
      const res = await axios.post(
        `/api/v1/project/${projectId}/share`,
        payload
      );
      return res.data;
    },
    onSuccess: (data: ShareProjectResponseBody): void => {
      setNotification({
        id: "project-shared-successfully",
        message: "Project Shared Successfully",
        details: data?.message,
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the project details
      queryClient.invalidateQueries({
        queryKey: ["/api/project/v1", projectId],
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An error occurred while sharing the project";
      setNotification({
        id: "project-share-failed",
        message: "Project Share Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const fetchVmetaCanvas = async (
  id: string
): Promise<VmetaProjectResponseBody> => {
  try {
    const res = await axios.get(`/api/v1/vmeta/canvas/${id}`);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useVmetaCanvas = (
  id: string
): UseQueryResult<VmetaProjectResponseBody, Error> => {
  return useQuery<VmetaProjectResponseBody, Error>({
    queryKey: ["/api/v1/vmeta/canvas", id],
    queryFn: async () => await fetchVmetaCanvas(id),
    retry: false,
    enabled: !!id,
  });
};

export const useDeleteVmetaCanvas = (): UseMutationResult<
  void,
  ErrorNotification,
  string
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<void, ErrorNotification, string>({
    mutationFn: async (id: string): Promise<void> => {
      await axios.delete(`/api/v1/vmeta/canvas/${id}`);
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "delete-vmeta-canvas-successfully",
        message: "Sample Tracker Deleted",
        details: "Sample Tracker has been deleted successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the canvas list
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/canvas/search"],
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An error occurred while deleting the Sample Tracker";
      setNotification({
        id: "delete-vmeta-canvas-failed",
        message: "Sample Tracker Deletion Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useCheckVmetaCanvasWritePermission = (): UseMutationResult<
  CheckVmetaCanvasWritePermissionResponseBody,
  ErrorNotification,
  { id: string }
> => {
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    CheckVmetaCanvasWritePermissionResponseBody,
    ErrorNotification,
    { id: string }
  >({
    mutationFn: async ({ id }: { id: string }) => {
      const res = await axios.post(
        `/api/v1/vmeta/canvas/${id}/checkWritePermission`
      );
      return res.data;
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "check-vmeta-canvas-write-permission-failed",
        message: "Write Permission Check Failed",
        details: error.response?.data?.message || "",
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};

export const useUpdateVmetaCanvas = (): UseMutationResult<
  VmetaProjectResponseBody,
  ErrorNotification,
  { id: string; data: UpdateVmetaProjectRequestBody }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();
  return useMutation<
    VmetaProjectResponseBody,
    ErrorNotification,
    { id: string; data: UpdateVmetaProjectRequestBody }
  >({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateVmetaProjectRequestBody;
    }): Promise<VmetaProjectResponseBody> => {
      const res = await axios.patch(`/api/v1/vmeta/canvas/${id}`, data);
      return res.data;
    },
    onSuccess: async (): Promise<void> => {
      setNotification({
        id: "update-vmeta-canvas-successfully",
        message: "Sample Tracker Updated",
        details: "Sample Tracker has been updated successfully",
        variant: NOTIFICATION.SUCCESS,
      });
      // Invalidate and refetch the canvas list
      await queryClient.invalidateQueries({
        queryKey: ["/api/v1/vmeta/canvas/search"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An error occurred while updating the Sample Tracker";
      setNotification({
        id: "update-vmeta-canvas-failed",
        message: "Sample Tracker Update Failed",
        details: errorMessage,
        variant: NOTIFICATION.ERROR,
      });
      throw error;
    },
  });
};
